const { uniq, chunk, pickBy, get, omit, pick, isEmpty } = require('lodash');
const { addDays, } = require('date-fns');
const moji = require('moji');

const { deliveryTypes, prefectures, boxSizes, deliveryTimes, } = require('../config');
const { idWithTenant } = require('../util');
const { computeInventory, } = require('./product');
const { computeAmazonProductSizeType, } = require('./amazonProduct');

const { entries } = Object;
const allowedFileExtensions = ['pdf'];
const validateFile = file => file != null && (allowedFileExtensions.includes(file.name.split('.').pop()));
const returnDestinations = {
  own: { label: '自社', },
  logisp: { label: '倉庫', },
};
const types = {
  normal: { label: '出荷依頼', color: 'info', quantityName: 'goodQuantity', opponentQuantityName: 'ngQuantity', },
  return: { label: '返送依頼', color: 'warning', quantityName: 'ngQuantity', opponentQuantityName: 'goodQuantity', },
  discard: { label: '廃棄依頼', color: 'warning', quantityName: 'ngQuantity', opponentQuantityName: 'goodQuantity', },
  amazon: { label: 'Amazon納品', color: 'success', quantityName: 'goodQuantity', opponentQuantityName: 'ngQuantity', },
};
const statuses = {
  initial: { label: '確認中', color: '', },
  pending: { label: '保留中', color: 'warning', },
  waitingToReceive: { label: '入荷待ち', color: 'warning', },
  waitingToShip: { label: '出荷待ち', color: '', },
  waitingToDiscard: { label: '廃棄待ち', color: '', },
  processing: { label: '出荷作業中', color: 'secondary', },
  shipped: { label: '出荷済み', color: 'success', },
  discarded: { label: '廃棄済み', color: 'success', },
  cancelling: { label: 'キャンセル申請中', color: 'warning', },
  cancelled: { label: 'キャンセル済み', color: 'secondary', },
};
const shippingOptions = {
  opp: { label: 'OPP梱包' },
  puchi: { label: 'プチプチ梱包' },
  flyer: { label: 'チラシ同封' },
  print: { label: '添付ファイルの印刷同封' },
  slip: { label: '納品書同封' },
  cashOnDelivery: { label: '着払いで出荷' },
  codAll: { label: '代引きで出荷' },
  gift: { label: 'ギフト' },
};
const deliveryType = (generalSetting = {}, { id, type, items, shippingOptionValues = [], postalCode, shippedType, isToBe = false, directedDeliveryType, }, productsById, allowsMultiNeko = false) => {
  if(directedDeliveryType) return directedDeliveryType;
  if(type === 'amazon') return 'amazon';
  if(isToBe) return generalSetting.toBeDeliveryCarrier || generalSetting.defaultDeliveryCarrier;

  const isCashOnDelivery = shippingOptionValues.includes('cashOnDelivery');
  const isCashOnDeliveryAll = shippingOptionValues.includes('codAll');
  const isNekoProduct = (productId) => get(productsById, [productId, 'isNeko']) || get(productsById, [productId, 'shippingSize']) === 'neko';
  if(!isCashOnDelivery && !isCashOnDeliveryAll) {
    if(items.length === 1 && items.every(_ => isNekoProduct(_.productId) && _.goodQuantity === 1)) {
      return 'neko';
    }
    if(allowsMultiNeko && items.every(_ => isNekoProduct(_.productId))) {
      return 'neko';
    }
  }

  const deliveryCarrier = Array(5).fill().map((_, i) => ({ deliveryCarrier: generalSetting[`rule${i + 1}DeliveryCarrier`], postalCodes: generalSetting[`rule${i + 1}PostalCodes`], shippingSizes: generalSetting[`rule${i + 1}ShippingSizes`], })).find(({ deliveryCarrier, postalCodes, shippingSizes, }) => {
    return [postalCodes, shippingSizes].some(_ => !isEmpty(_)) && (
      (isEmpty(postalCodes) || postalCodes.split('\n').some(_ => postalCode?.startsWith(_))) &&
      (isEmpty(shippingSizes) || items.length === 1 && items.every(_ => shippingSizes.includes(productsById[_.productId]?.shippingSize) && _.goodQuantity === 1))
    );
  })?.deliveryCarrier || generalSetting.defaultDeliveryCarrier || 'sagawa';
  return `${shippedType || deliveryCarrier}${isCashOnDeliveryAll ? '-cod-all' : isCashOnDelivery ? '-cod' : ''}`;
};

const computeGBFcSortNumber = (amazonFcData, shippingRequest) => {
  const number = amazonFcData.find(_ => _['コード'] === formatPlanOfShippingRequest(shippingRequest).plan.DestinationFulfillmentCenterId)?.['GBナンバー'];
  // NOTE: FCマスタ未登録・GBナンバー未設定の場合は空文字を返す（呼び出し側で事前チェック済み）
  if(isEmpty(number)) return '';

  const [a, b, c, d, e] = Array.from(number).map(_ => parseInt(_, 10));
  const surplus = (a * 3 + b + c * 3 + d + e * 3) % 10;
  const checkDigit = surplus === 0 ? 0 : 10 - surplus;
  return number + checkDigit;
}

const computeDocumentNumberForBarcode = (shippingRequest) => {
  const documentNumber = get(shippingRequest, 'shippingItems[0].documentNumber', '');

  const checkDigit = parseInt(documentNumber, 10) % 7;
  return `${documentNumber}${checkDigit}`;
};

const computeCsvYamatoBoxSize = (row) => {
  return row['サイズ'].match(/\d/) ? `size${moji(row['サイズ']).convert('ZS', 'HS').toString()}` : 'neko';
};

const computeCsvSagawaBoxSize = (row) => {
  if(row['明細１重量区分(文字表記)'] == null) return [];

  return ['１', '２', '３', '４', '５', '６', '７', '８'].flatMap((number) => {
    const size = row[`明細${number}重量区分(文字表記)`].match('サイズ') ? (() => {
      const value = parseInt(row[`明細${number}重量`], 10);
      return (
        (0 < value && value <= 60) ? 'size60' :
        (60 < value && value <= 80) ? 'size80' :
        (80 < value && value <= 100) ? 'size100' :
        (100 < value && value <= 140) ? 'size140' :
        (140 < value && value <= 160) ? 'size160' :
        (160 < value && value <= 170) ? 'size170' :
        (170 < value && value <= 180) ? 'size180' :
        (180 < value && value <= 200) ? 'size200' :
        (200 < value && value <= 220) ? 'size220' :
        (220 < value && value <= 240) ? 'size240' :
        (240 < value && value <= 260) ? 'size260' :
        null
      );
    })() : ({
      2: 'size60',
      5: 'size80',
      10: 'size100',
      20: 'size140',
      30: 'size160',
      50: 'size170',
      60: 'size180',
    })[row[`明細${number}重量`]];
    return Array(parseInt(row[`明細${number}個数`], 10)).fill().map(_ => size);
  });
};

const computeAmazonShippingRequestAllItems = (products, uncompletedShippingRequests, amazonProductSizeType) => {
  return products
    .filter(_ => _.isAmazonProduct && (_.deliveryRoute || 'amazon') === 'amazon' && !isEmpty(_.fnsku) && !_.productTagIds?.includes('inboundShippmentError') && (amazonProductSizeType != null ? computeAmazonProductSizeType(_.amazonProduct) === amazonProductSizeType : true))
    .map((product) => {
      const { goodInventoryWithoutAllocation } = computeInventory(product, uncompletedShippingRequests);
      return {
        productId: product.id,
        sku: product.sku,
        goodQuantity: goodInventoryWithoutAllocation,
        quantity: goodInventoryWithoutAllocation,
      };
    }).filter(_ => _.goodQuantity > 0);
};

const computeInboundShipmentErrorSkus = (e) => {
  const errorSkus = [
    ...(e.message.match(/are not valid: \[([^\]]+)\]/)?.[1]?.split(', ') ?? []),
    ...(e.message.split('\n').map(_ => _.match(/input error with the resource '([^']+)'/)?.[1]) ?? []),
  ].filter(_ => _);
  return errorSkus;
};

const computeCodFee = (feesText, amount) => {
  const feeAmount = feesText
    .split('\n')
    .filter(_ => _)
    .map(_ => [...(_.match(/^(\d+)-(\d*):(\d+)$/) || [])].slice(1).map(_ => parseInt(_, 10)))
    .map(_ => console.log(_) || _)
    .find(_ => _[0] <= amount && (!_[1] || amount <= _[1]))
    ?.[2];
  return feeAmount;
};

const fields = ({ isNew, originalStatus, tenant, senders = [], type, } = {}) => {
  return ['discard'].includes(type) ? {} : {
    ...(
      type !== 'amazon' && {
        postalCode: {
          type: 'string',
          label: '郵便番号',
          validations: {
            required: v => !isEmpty(v),
            format: v => (v || '').match(/^[0-9]{7}$/g),
          },
          inputProps: {
            type: 'tel',
          },
          format: _ => _.replace(/-/g, ''),
        },
        prefecture: {
          label: '都道府県',
          type: 'select',
          options: entries(prefectures).map(([k, v]) => ({ value: k, label: v })),
          validations: {
            required:  v => !isEmpty(v),
          },
        },
        city: {
          type: 'string',
          label: '市区郡町村',
          validations: {
            required: v => !isEmpty(v),
          },
        },
        address1: {
          type: 'string',
          label: 'それ以降の住所',
          validations: {
            required: v => !isEmpty(v),
          },
        },
        address2: {
          type: 'string',
          label: '住所2',
          hidden: _ => isEmpty(_.address2),
        },
        name: {
          label: '宛名',
          type: 'string',
          validations: {
            required: v => !isEmpty(v),
          },
        },
        phone: {
          type: 'string',
          inputType: 'tel',
          label: '電話番号',
          validations: {
            format: v => !v || v.match(/^(0{1}\d{9,10})$/),
          },
          placeholder: '08012345678 （ハイフンなし）',
          format: _ => _.replace(/-/g, ''),
        },
        deliveryDate: {
          type: 'date',
          label: '配送日',
          placeholder: '指定なし',
          inputProps: {
            minDate: addDays(new Date(), 2),
          },
          hint: '北海道、沖縄宛ての場合は最短で3日後のお届けとなります。',
        },
        deliveryTime: {
          type: 'select',
          label: '配送時間',
          options: entries(omit(pickBy(deliveryTimes, _ => ['sagawa', 'all'].includes(_.type)), ['sagawa1821'])).map(([k, _]) => ({ label: _.label, value: k })),
          validations: {
            required: v => !isEmpty(v),
          },
          initialValue: 'free',
        },
        ...(
          type === 'normal' && {
            shippingOptionValues: {
              label: 'オプション',
              type: 'multiSelect',
              options: entries(omit(shippingOptions, (tenant?.tenantOptionValues?.includes('codAllEnabled') ? [] : ['codAll']))).map(([k, _]) => ({ label: _.label, value: k, })),
              initialValue: [...(tenant?.slipDefaultEnabled ? ['slip'] : [])],
              hint: '複数選択できます',
            },
            files: {
              label: 'PDFファイル',
              type: 'files',
              validations: {
                required: _ => !isEmpty(_),
                fileType: _ => Array.from(_ || []).every(validateFile),
              },
              inputProps: {
                accept: 'application/pdf',
              },
              hidden: _ => !(_.shippingOptionValues || []).includes('print'),
            },
            codAmount: {
              label: '代引き金額',
              type: 'integer',
              validations: {
                greaterThan0: _ => _ > 0,
              },
              hidden: _ => !(_.shippingOptionValues || []).includes('codAll'),
            },
            orderNumber: {
              label: '注文番号',
              type: 'string',
              hint: '複数ある場合は「,」カンマで区切って入力してください。',
            },
          }
        ),
        ...(
          ['normal', 'return'].includes(type) && {
            isToBe: {
              label: 'toB出荷',
              type: 'boolean',
              initialValue: false,
            },
          }
        ),
      }
    ),
    senderId: {
      label: '配送元',
      type: 'select',
      options: senders.map(_ => ({ label: _.code + (_.isDefault ? ' (デフォルト)' : ''), value: _.id })),
      validations: {
        required: _ => !isEmpty(_),
      },
      initialValue: get(senders.find(_ => _.isDefault), 'id'),
    },
    ...(
      !isNew && {
        status: {
          label: 'ステータス',
          type: 'select',
          options: entries(pick(statuses, [originalStatus, 'initial', 'pending'])).map(([k, _]) => ({ label: _.label, value: k })),
          validations: {
            required: _ => !isEmpty(_),
          },
          initialValue: 'initial',
        },
      }
    ),
    note: {
      label: 'メモ',
      type: 'text',
      hint: '倉庫側には確認されません',
    },
  };
};

const adminFields = ({ values, type, } = {}) => {
  const candidateStatues = ({
    normal: omit(statuses, ['waitingToDiscard', 'discarded']),
    return: omit(statuses, ['waitingToDiscard', 'discarded']),
    discard: pick(statuses, ['waitingToDiscard', 'discarded']),
    amazon: omit(statuses, ['waitingToDiscard', 'discarded']),
  })[type];
  return {
    status: {
      label: 'ステータス',
      type: 'select',
      options: entries(candidateStatues).map(([k, _]) => ({ label: _.label, value: k })),
      validations: {
        required: _ => !isEmpty(_),
      },
      initialValue: 'initial',
    },
    adminNote: {
      label: '管理者メモ',
      type: 'text',
    },
    isHiddenDeliveryType: {
      label: '配送方法非表示',
      type: 'boolean',
      initialValue: false,
    },
    directedDeliveryType: {
      label: '強制指定配送方法',
      type: 'select',
      options: entries(deliveryTypes).map(([k, v]) => ({ label: v.label, value: k })),
      hint: '空の場合は、計算された配送方法',
    },
  };
};

const formatPlanOfShippingRequest = (shippingRequest) => {
  const { destination } = shippingRequest.plan ?? {};
  return {
    ...shippingRequest,
    plan: {
      ...(
        destination != null && {
          DestinationFulfillmentCenterId: destination.warehouseId,
          ShipToAddress: {
            PostalCode: destination.address.postalCode,
            StateOrProvinceCode: destination.address.stateOrProvinceCode,
            City: destination.address.city,
            AddressLine1: destination.address.addressLine1,
            AddressLine2: destination.address.addressLine2,
            Name: destination.address.name,
          },
        }
      ),
      ...shippingRequest.plan,
    },
  };
};

module.exports = {
  fields,
  itemFields: ({ items = [], products = [], type = 'normal', } = {}) => {
    const filteredProducts = type === 'amazon' ? products.filter(_ => _.isAmazonProduct && (_.isCase || !isEmpty(_.fnsku))) : products;
    return {
      productId: {
        label: '商品',
        type: 'select',
        options: filteredProducts.map(_ => ({ label: `${idWithTenant(_.ref)} [${_.sku}] ${_.name}`, value: _.id })),
        validations: {
          required: _ => !isEmpty(_),
          unique: v => items.filter(_ => _.productId === v).length < 2,
        },
        placeholder: '商品名、SKU、ASIN、FNSKU、JANで検索できます(検索結果は最大300個まで表示されます)',
      },
      [types[type].quantityName]: {
        label: '数量',
        type: 'integer',
        validations: {
          greaterThan0: _ => _ > 0,
        },
      },
    };
  },
  boxItemFields: ({ shippingRequest, } = {}) => {
    const productIds = uniq(shippingRequest.items.map(_ => _.productId));
    return {
      productId: {
        label: '商品',
        type: 'select',
        options: productIds.map(_ => ({ label: _, value: _ })),
        validations: {
          required: _ => !isEmpty(_),
        },
      },
      quantity: {
        label: '数量',
        type: 'integer',
        validations: {
          greaterThan0: _ => _ > 0,
        },
      },
    };
  },
  shippingItemFields: ({ type, products = [] } = {}) => {
    return {
      documentNumber: {
        label: '伝票番号',
        type: 'string',
        validations: {
          required: _ => _ > 0 || !isEmpty(_),
        },
      },
      boxSize: {
        label: 'サイズ',
        type: 'select',
        options: entries(boxSizes).map(([k, _]) => ({ label: _.label, value: k })),
      },
      ...(
        type === 'amazon' && {
          quantity: {
            label: '数量',
            type: 'integer',
            initialValue: 1,
          },
        }
      ),
      shippedOn: {
        label: '出荷日',
        type: 'date',
      },
    };
  },
  adminFields,
  statuses,
  boxSizes,
  deliveryType,
  deliveryTypes,
  shippingOptions,
  types,
  returnDestinations,
  computeGBFcSortNumber,
  computeDocumentNumberForBarcode,
  computeCsvYamatoBoxSize,
  computeCsvSagawaBoxSize,
  computeAmazonShippingRequestAllItems,
  computeInboundShipmentErrorSkus,
  computeCodFee,
  formatPlanOfShippingRequest,
};
